import React from "react";
import {
  FaEdit,
  FaCheck,
  FaTimes,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaShieldAlt,
} from "react-icons/fa";
import { Card } from "../UI/DesignStandards";
import { PrimaryButton, SecondaryButton } from "../UI/DesignStandards/Buttons";
import { OTPCodeInput } from "../UI/DesignStandards/Forms";

const PasswordSection = ({
  editingPassword,
  loading,
  errors,
  formData,
  passwordChecks,
  showPassword,
  showConfirmPassword,
  hasPassword, // null = unknown, true = has password, false = no password
  requireOTP,
  sendingOTP,
  otpError,
  userEmail,
  otpCode,
  otpCodeValid,
  otpCodeSent, // Whether OTP code has been sent
  onEdit,
  onSave,
  onCancel,
  onChange,
  onTogglePassword,
  onToggleConfirmPassword,
  onSendOTP,
  onOTPCodeChange,
  onResendOTP,
}) => {
  // Determine if user is setting or changing password
  // If hasPassword is explicitly false, they're setting
  // If hasPassword is null (unknown) and no current password entered, assume setting
  // If hasPassword is true, they're changing
  const isSettingPassword =
    hasPassword === false ||
    (hasPassword === null && !formData.currentPassword);

  // OTP-first flow: Show OTP input first, then password fields after code is entered
  if (editingPassword && requireOTP && !otpCodeValid) {
    return (
      <Card className="mb-0" aria-labelledby="password-heading">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div
              className="w-10 h-10 bg-brand-cream rounded-lg flex items-center justify-center"
              aria-hidden="true"
            >
              <FaLock className="h-5 w-5 text-brand-muted" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3
                  id="password-heading"
                  className="heading-display text-lg"
                >
                  {isSettingPassword ? "Set Password" : "Change Password"}
                </h3>
              </div>
              <SecondaryButton
                onClick={onCancel}
                className="!px-3 !py-2 text-sm"
                aria-label="Cancel password change"
              >
                Cancel
              </SecondaryButton>
            </div>

            {/* Show different UI based on whether code has been sent */}
            {!otpCodeSent ? (
              <>
                {/* Info message - shown before code is sent */}
                <div className="mb-6">
                  <div className="p-4 card-inset">
                    <p className="text-sm text-brand-charcoal">
                      For security, we need to verify your email address before{" "}
                      {isSettingPassword ? "setting" : "changing"} your
                      password. Click the button below to receive a verification
                      code.
                    </p>
                  </div>
                </div>

                {/* Send Code Button - shown before code is sent */}
                <div className="mt-4">
                  <SecondaryButton
                    onClick={onSendOTP}
                    disabled={sendingOTP}
                    loading={sendingOTP}
                    className="w-full !px-3 !py-2 text-sm"
                  >
                    {sendingOTP ? "Sending..." : "Send Verification Code"}
                  </SecondaryButton>
                </div>
              </>
            ) : (
              <>
                {/* Info message - shown when code is sent */}
                <div className="mb-4">
                  <div className="p-3 card-inset">
                    <p className="text-sm text-brand-charcoal">
                      Enter the 6-digit code below. Password fields will appear
                      automatically when the code is complete.
                    </p>
                  </div>
                </div>

                {/* OTP Input - shown after code is sent */}
                <OTPCodeInput
                  email={userEmail}
                  onVerify={(code) => {
                    // When user clicks "Verify Code", ensure code is stored and validated
                    // This is redundant with onCodeChange but provides explicit user action
                    onOTPCodeChange(code);
                  }}
                  onCodeChange={onOTPCodeChange} // Real-time validation as user types
                  onBack={onCancel}
                  onResend={onResendOTP}
                  loading={sendingOTP}
                  verifying={false} // Don't show verifying state - backend will verify
                  error={otpError}
                  hideVerifyButton={true} // Hide verify button since we do real-time validation
                />
              </>
            )}
          </div>
        </div>
      </Card>
    );
  }
  return (
    <Card className="mb-0" aria-labelledby="password-heading">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <div
            className="w-10 h-10 bg-brand-cream rounded-lg flex items-center justify-center"
            aria-hidden="true"
          >
            <FaLock className="h-5 w-5 text-brand-muted" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3
                id="password-heading"
                className="heading-display text-lg"
              >
                Password
              </h3>
            </div>
            {!editingPassword ? (
              <SecondaryButton
                onClick={onEdit}
                icon={FaEdit}
                className="!px-3 !py-2 text-sm"
                aria-label={
                  hasPassword === false ? "Set password" : "Change password"
                }
              >
                {hasPassword === false
                  ? "Set Password"
                  : hasPassword === true
                  ? "Change Password"
                  : "Set/Change Password"}
              </SecondaryButton>
            ) : (
              <div className="flex space-x-2">
                <PrimaryButton
                  onClick={onSave}
                  disabled={loading}
                  loading={loading}
                  loadingText="Updating..."
                  aria-label={loading ? "Updating password" : "Update password"}
                >
                  Update
                </PrimaryButton>
                <SecondaryButton
                  onClick={onCancel}
                  className="!px-3 !py-2 text-sm"
                  aria-label="Cancel password changes"
                >
                  Cancel
                </SecondaryButton>
              </div>
            )}
          </div>

          {/* Show OTP input if code is sent but not yet valid */}
          {editingPassword && requireOTP && otpCodeSent && !otpCodeValid && (
            <div className="mt-6">
              <OTPCodeInput
                email={userEmail}
                onVerify={(code) => {
                  onOTPCodeChange(code);
                }}
                onCodeChange={onOTPCodeChange}
                onBack={onCancel}
                onResend={onResendOTP}
                loading={sendingOTP}
                verifying={false}
                error={otpError}
                hideVerifyButton={true}
              />
            </div>
          )}

          {/* Password fields - shown after OTP code format is validated (6 digits) */}
          {/* Keep fields visible even if there's an error, so user can correct the code */}
          {editingPassword && requireOTP && otpCodeValid && (
            <div className="mt-6 space-y-6">
              {/* Show OTP input again if there's an error, so user can correct the code */}
              {otpError && (
                <>
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800 font-medium mb-1">
                      Verification Failed
                    </p>
                    <p className="text-sm text-red-700">{otpError}</p>
                    <p className="text-xs text-red-600 mt-2">
                      You can correct the code below and try again. Your
                      password will be preserved.
                    </p>
                  </div>

                  {/* OTP Input - shown again when there's an error so user can correct */}
                  <div className="mb-6">
                    <OTPCodeInput
                      email={userEmail}
                      onVerify={(code) => {
                        onOTPCodeChange(code);
                      }}
                      onCodeChange={onOTPCodeChange}
                      onBack={onCancel}
                      onResend={onResendOTP}
                      loading={sendingOTP}
                      verifying={false}
                      error={otpError}
                      hideVerifyButton={true}
                    />
                  </div>
                </>
              )}

              {/* New Password */}
              <div>
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-medium text-brand-charcoal mb-2"
                >
                  {isSettingPassword ? "Password" : "New Password"}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={onChange}
                    className={`block w-full px-4 py-3 border ${
                      errors.newPassword
                        ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                        : "border-brand-charcoal/20 focus:ring-brand-gold-ink"
                    } rounded-lg shadow-sm focus:outline-none focus:ring-2 transition-colors duration-200`}
                    placeholder="Enter new password"
                    aria-describedby={
                      errors.newPassword
                        ? "new-password-error"
                        : "password-requirements"
                    }
                    aria-invalid={errors.newPassword ? "true" : "false"}
                  />
                  <button
                    type="button"
                    tabIndex="-1"
                    onClick={onTogglePassword}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-brand-muted hover:text-brand-charcoal focus:outline-none"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <FaEyeSlash className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <FaEye className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
                {errors.newPassword && (
                  <p
                    id="new-password-error"
                    className="mt-1 text-sm text-red-600"
                    role="alert"
                  >
                    {errors.newPassword}
                  </p>
                )}

                {/* Password Requirements */}
                <div
                  className="mt-4 p-4 card-inset"
                  id="password-requirements"
                  role="region"
                  aria-label="Password requirements"
                >
                  <div className="flex items-center mb-3">
                    <FaShieldAlt
                      className="h-4 w-4 text-brand-muted mr-2"
                      aria-hidden="true"
                    />
                    <span className="text-sm font-medium text-brand-charcoal">
                      Password Requirements
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="flex items-center text-sm">
                      {passwordChecks.length ? (
                        <FaCheck
                          className="text-brand-charcoal mr-2 h-4 w-4"
                          aria-hidden="true"
                        />
                      ) : (
                        <FaTimes
                          className="text-red-500 mr-2 h-4 w-4"
                          aria-hidden="true"
                        />
                      )}
                      <span
                        className={
                          passwordChecks.length
                            ? "text-brand-charcoal"
                            : "text-brand-muted"
                        }
                      >
                        At least 8 characters
                      </span>
                    </div>
                    <div className="flex items-center text-sm">
                      {passwordChecks.uppercase ? (
                        <FaCheck
                          className="text-brand-charcoal mr-2 h-4 w-4"
                          aria-hidden="true"
                        />
                      ) : (
                        <FaTimes
                          className="text-red-500 mr-2 h-4 w-4"
                          aria-hidden="true"
                        />
                      )}
                      <span
                        className={
                          passwordChecks.uppercase
                            ? "text-brand-charcoal"
                            : "text-brand-muted"
                        }
                      >
                        One uppercase letter
                      </span>
                    </div>
                    <div className="flex items-center text-sm">
                      {passwordChecks.lowercase ? (
                        <FaCheck
                          className="text-brand-charcoal mr-2 h-4 w-4"
                          aria-hidden="true"
                        />
                      ) : (
                        <FaTimes
                          className="text-red-500 mr-2 h-4 w-4"
                          aria-hidden="true"
                        />
                      )}
                      <span
                        className={
                          passwordChecks.lowercase
                            ? "text-brand-charcoal"
                            : "text-brand-muted"
                        }
                      >
                        One lowercase letter
                      </span>
                    </div>
                    <div className="flex items-center text-sm">
                      {passwordChecks.number ? (
                        <FaCheck
                          className="text-brand-charcoal mr-2 h-4 w-4"
                          aria-hidden="true"
                        />
                      ) : (
                        <FaTimes
                          className="text-red-500 mr-2 h-4 w-4"
                          aria-hidden="true"
                        />
                      )}
                      <span
                        className={
                          passwordChecks.number
                            ? "text-brand-charcoal"
                            : "text-brand-muted"
                        }
                      >
                        One number
                      </span>
                    </div>
                    <div className="flex items-center text-sm md:col-span-2">
                      {passwordChecks.special ? (
                        <FaCheck
                          className="text-brand-charcoal mr-2 h-4 w-4"
                          aria-hidden="true"
                        />
                      ) : (
                        <FaTimes
                          className="text-red-500 mr-2 h-4 w-4"
                          aria-hidden="true"
                        />
                      )}
                      <span
                        className={
                          passwordChecks.special
                            ? "text-brand-charcoal"
                            : "text-brand-muted"
                        }
                      >
                        One special character
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-brand-charcoal mb-2"
                >
                  {isSettingPassword
                    ? "Confirm Password"
                    : "Confirm New Password"}
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={onChange}
                    className={`block w-full px-4 py-3 border ${
                      errors.confirmPassword
                        ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                        : "border-brand-charcoal/20 focus:ring-brand-gold-ink"
                    } rounded-lg shadow-sm focus:outline-none focus:ring-2 transition-colors duration-200`}
                    placeholder="Confirm new password"
                    aria-describedby={
                      errors.confirmPassword
                        ? "confirm-password-error"
                        : undefined
                    }
                    aria-invalid={errors.confirmPassword ? "true" : "false"}
                  />
                  <button
                    type="button"
                    tabIndex="-1"
                    onClick={onToggleConfirmPassword}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-brand-muted hover:text-brand-charcoal focus:outline-none"
                    aria-label={
                      showConfirmPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showConfirmPassword ? (
                      <FaEyeSlash className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <FaEye className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p
                    id="confirm-password-error"
                    className="mt-1 text-sm text-red-600"
                    role="alert"
                  >
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default PasswordSection;
